var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import isEqual from 'lodash.isequal';
import moment from 'moment';
import { InvoiceStatus, Placement, } from '@wix/ambassador-wix-quotes-web/types';
import { addressFormatter } from '@wix/crm-financial-formatters';
import { getCurrencyByKey } from '@wix/locale-dataset-javascript';
import { storeProductVariantAppendix } from '../../utils/regex';
import { parseJSON, parseSerializedValue } from '../../utils/parsing';
export var getFormattedIssueDate = function (_a) {
    var isRecurring = _a.isRecurring, issueDate = _a.issueDate, status = _a.status;
    var shouldModifyDatesForRecurringSpecialCase = isRecurring && status === InvoiceStatus.Draft;
    return shouldModifyDatesForRecurringSpecialCase
        ? moment().startOf('day').utc().toDate()
        : new Date(issueDate);
};
var diffInDaysFloored = function (_a) {
    var dateA = _a.dateA, dateB = _a.dateB;
    return Math.floor(moment.duration(moment(dateA).diff(dateB)).as('days'));
};
export var getFormattedDueDate = function (_a) {
    var dueDate = _a.dueDate, isRecurring = _a.isRecurring, issueDate = _a.issueDate, status = _a.status;
    var shouldModifyDatesForRecurringSpecialCase = isRecurring && status === InvoiceStatus.Draft;
    var dueDateOffset = shouldModifyDatesForRecurringSpecialCase
        ? diffInDaysFloored({
            dateA: dueDate,
            dateB: issueDate,
        })
        : null;
    return shouldModifyDatesForRecurringSpecialCase
        ? moment().add(dueDateOffset, 'days').startOf('day').utc().toDate()
        : new Date(dueDate);
};
var mapLineItemMetadata = function (_a) {
    var _b, _c;
    var metadata = _a.metadata;
    var metadataJsonMap = (_b = metadata === null || metadata === void 0 ? void 0 : metadata.metadata) !== null && _b !== void 0 ? _b : {};
    var selectedOptions = (_c = parseJSON(metadataJsonMap.selectedOptions)) === null || _c === void 0 ? void 0 : _c.reduce(function (accObj, _a) {
        var option = _a.option, selection = _a.selection;
        if (option && selection) {
            accObj[option] = selection;
        }
        return accObj;
    }, {});
    try {
        return __assign(__assign({}, metadataJsonMap), (selectedOptions ? { selectedOptions: selectedOptions } : {}));
    }
    catch (_d) {
        return metadataJsonMap;
    }
};
export var mapLineItemDtoToViewModelMapper = function (lineItems) {
    return lineItems.length
        ? lineItems.map(function (lineItem) {
            var _a, _b, _c;
            var metadata = mapLineItemMetadata(lineItem);
            return {
                id: lineItem.id,
                name: lineItem.name,
                description: (_a = lineItem.description) === null || _a === void 0 ? void 0 : _a.replace(storeProductVariantAppendix(Object.keys((_b = metadata.selectedOptions) !== null && _b !== void 0 ? _b : {})), ''),
                metadata: metadata,
                price: parseSerializedValue(lineItem.price) || 0,
                quantity: parseSerializedValue(lineItem.quantity),
                taxedTotal: parseSerializedValue(lineItem.taxedTotal),
                taxes: ((_c = lineItem.taxes) === null || _c === void 0 ? void 0 : _c.length)
                    ? lineItem.taxes.map(function (tax) { return ({
                        name: tax.name,
                        rate: parseSerializedValue(tax.rate) || 0,
                        taxId: tax.code,
                    }); })
                    : [],
                source: lineItem.source,
            };
        })
        : [];
};
export var mapInvoiceDetailsFromDTO = function (_a) {
    var _b, _c, _d;
    var invoice = _a.invoice, recurringInvoiceViewEnabled = _a.recurringInvoiceViewEnabled, _e = _a.status, status = _e === void 0 ? InvoiceStatus.Draft : _e;
    var _f = invoice !== null && invoice !== void 0 ? invoice : {}, currencyCode = _f.currencyCode, _g = _f.customer, customer = _g === void 0 ? {} : _g, _h = _f.dates, dates = _h === void 0 ? {} : _h, _j = _f.deposit, deposit = _j === void 0 ? {} : _j, _k = _f.discount, discount = _k === void 0 ? {} : _k, _l = _f.dynamicTotals, dynamicTotals = _l === void 0 ? {} : _l, _m = _f.lineItems, lineItems = _m === void 0 ? {} : _m, _o = _f.locale, locale = _o === void 0 ? {} : _o, _p = _f.metaData, metaData = _p === void 0 ? {} : _p, _q = _f.number, invoiceNumber = _q === void 0 ? '' : _q, _r = _f.taxes, calculatedTaxes = _r === void 0 ? {} : _r, _s = _f.title, title = _s === void 0 ? '' : _s, _t = _f.totals, totals = _t === void 0 ? {} : _t;
    var depositRate = Number(deposit.percentage) || 0;
    return {
        balance: parseSerializedValue(dynamicTotals.balance) || 0,
        currency: currencyCode,
        customer: customer,
        deposit: {
            amount: Number(deposit.flatAmount) || 0,
            rate: (depositRate * 100).toFixed(getCurrencyByKey(currencyCode).fraction),
            type: deposit.type,
        },
        discount: {
            amount: parseSerializedValue(totals.discountAmount) || 0,
            rate: parseSerializedValue(discount.percentage) || 0,
        },
        dueDate: getFormattedDueDate({
            dueDate: dates.dueDate,
            isRecurring: !!recurringInvoiceViewEnabled,
            issueDate: dates.issueDate,
            status: status,
        }),
        fees: ((_b = totals.fees) === null || _b === void 0 ? void 0 : _b.length)
            ? totals.fees
                .map(function (fee) { return ({
                name: fee.name,
                price: parseSerializedValue(fee.price) || 0,
            }); })
                .filter(function (fee) { return fee.price > 0; })
            : [],
        invoiceNumber: invoiceNumber,
        issueDate: getFormattedIssueDate({
            isRecurring: !!recurringInvoiceViewEnabled,
            issueDate: dates.issueDate,
            status: status,
        }),
        lineItems: ((_c = lineItems.lineItems) === null || _c === void 0 ? void 0 : _c.length)
            ? mapLineItemDtoToViewModelMapper(lineItems.lineItems)
            : [],
        locale: locale.language,
        metaData: metaData,
        paid: parseSerializedValue(dynamicTotals.paidAmount) || 0,
        status: status,
        subtotal: parseSerializedValue(totals.subtotal) || 0,
        taxes: ((_d = calculatedTaxes.taxes) === null || _d === void 0 ? void 0 : _d.length)
            ? calculatedTaxes.taxes.map(function (tax) { return ({
                amount: parseSerializedValue(tax.taxed) || 0,
                name: tax.name,
                rate: parseSerializedValue(tax.rate) || 0,
                taxable: parseSerializedValue(tax.taxable),
            }); })
            : [],
        title: title,
        total: parseSerializedValue(totals.total) || 0,
    };
};
export var formatBusinessAddress = function (_a, t) {
    var _b, _c;
    var address = _a.address, description = _a.description;
    if (!address) {
        return [];
    }
    var localizedLocation = addressFormatter({
        city: address.city,
        country: address.country,
        state: address.subdivision,
        street: address.addressLine,
        streetName: (_b = address.streetAddress) === null || _b === void 0 ? void 0 : _b.name,
        streetNumber: (_c = address.streetAddress) === null || _c === void 0 ? void 0 : _c.number,
        zip: address.postalCode,
        t: t,
    });
    switch (description === null || description === void 0 ? void 0 : description.placement) {
        case Placement.Replace:
            return [description ? description.content : localizedLocation];
        case Placement.Before:
            return [description.content, localizedLocation];
        case Placement.After:
            return [localizedLocation, description.content];
        default:
            return [localizedLocation];
    }
};
// TODO: review if it is possible to simplify
export var formatCustomerAddresses = function (_a, t, newSubdivisionFormatEnabled) {
    var address = _a.address, billingAddress = _a.billingAddress, company = _a.company, email = _a.email, name = _a.name, phone = _a.phone, shippingAddress = _a.shippingAddress;
    var formatAddress = function (args) {
        return addressFormatter(__assign({ t: t, newSubdivisionFormatEnabled: newSubdivisionFormatEnabled }, args));
    };
    var addressLocalizedLocation = address &&
        formatAddress({
            city: address.city,
            country: address.country,
            state: address.state,
            street: address.street,
            zip: address.zip,
        });
    var billingAddressLocalizedLocation = billingAddress &&
        formatAddress({
            city: billingAddress.city,
            country: billingAddress.country,
            state: billingAddress.subdivision,
            street: billingAddress.addressLine,
            zip: billingAddress.postalCode,
        });
    var shippingLocalizedAddress = shippingAddress &&
        formatAddress({
            city: shippingAddress.city,
            country: shippingAddress.country,
            state: shippingAddress.subdivision,
            street: shippingAddress.addressLine,
            zip: shippingAddress.postalCode,
        });
    var shouldDisplayShippingAddress = !!(shippingAddress &&
        shippingLocalizedAddress &&
        !isEqual(shippingAddress, billingAddress) &&
        !isEqual(shippingLocalizedAddress, addressLocalizedLocation));
    var shouldDisplayCustomerAddress = (billingAddressLocalizedLocation || addressLocalizedLocation) !== '';
    var hasAddress = !!(shouldDisplayCustomerAddress || shouldDisplayShippingAddress);
    var infoSections = [];
    var recipient = [name, company.name].filter(Boolean).join('\n');
    if (shouldDisplayCustomerAddress) {
        infoSections.push({
            title: t('price-quote.view.customer.billing-address-title'),
            subtitle: recipient,
            content: [billingAddressLocalizedLocation || addressLocalizedLocation],
        });
    }
    if (shouldDisplayShippingAddress) {
        infoSections.push({
            title: !shouldDisplayCustomerAddress
                ? t('price-quote.view.customer.billing-address-title')
                : t('price-quote.view.customer.shipping-address-title'),
            subtitle: shouldDisplayCustomerAddress ? '' : recipient,
            content: [shippingLocalizedAddress],
        });
    }
    infoSections.push({
        title: hasAddress
            ? t('price-quote.view.customer.additional-info-title')
            : t('price-quote.view.customer.customer-info-title'),
        subtitle: hasAddress ? '' : recipient,
        content: [
            email.address,
            (phone === null || phone === void 0 ? void 0 : phone.number) &&
                t('price-quote.view.customer.phone-title', {
                    phoneNumber: phone.number,
                }),
            (company === null || company === void 0 ? void 0 : company.id) && "".concat(t('quote.form.vat_id'), ": ").concat(company.id),
        ],
    });
    return infoSections;
};
//# sourceMappingURL=utils.js.map